import { isPdfMimeType } from 'components/Receipts/receiptImageUrl';

type LocalReceiptPreviewEntry = {
  // Only held until a thumbnail exists; the full upload file must not outlive the preview it feeds.
  file?: File;
  keys: Set<string>;
  previewUrl?: string;
};

const renderedPdfPreviews = new WeakMap<File, Blob>();
const previewEntriesByFile = new WeakMap<File, Set<LocalReceiptPreviewEntry>>();
const previewEntriesByKey = new Map<string, LocalReceiptPreviewEntry>();

export const isReceiptPdfFile = (file: File): boolean =>
  isPdfMimeType(file.type) || file.name.toLocaleLowerCase().endsWith('.pdf');

const detachPreviewFile = (entry: LocalReceiptPreviewEntry): void => {
  if (entry.file) {
    previewEntriesByFile.get(entry.file)?.delete(entry);
    entry.file = undefined;
  }
};

const getOrCreatePreviewUrl = (entry: LocalReceiptPreviewEntry, preview: Blob): string | undefined => {
  if (!entry.previewUrl && typeof URL.createObjectURL === 'function') {
    entry.previewUrl = URL.createObjectURL(preview);
  }
  if (entry.previewUrl) {
    detachPreviewFile(entry);
  }
  return entry.previewUrl;
};

export const getRenderedLocalReceiptPdfPreview = (file: File): Blob | undefined => renderedPdfPreviews.get(file);

export const cacheLocalReceiptPdfPreview = (file: File, preview: Blob): string | undefined => {
  renderedPdfPreviews.set(file, preview);
  let previewUrl: string | undefined;
  new Set(previewEntriesByFile.get(file)).forEach((entry) => {
    previewUrl = getOrCreatePreviewUrl(entry, preview) ?? previewUrl;
  });
  return previewUrl;
};

// Nothing more can be rendered from a file whose preview failed, so stop retaining it.
export const discardLocalReceiptPreviewFile = (file: File): void => {
  new Set(previewEntriesByFile.get(file)).forEach(detachPreviewFile);
};

export const registerLocalReceiptPreview = (fileUri: string, file: File): void => {
  if (!isReceiptPdfFile(file)) {
    return;
  }

  const entry: LocalReceiptPreviewEntry = { file, keys: new Set([fileUri]) };
  previewEntriesByKey.set(fileUri, entry);
  const entriesForFile = previewEntriesByFile.get(file) ?? new Set<LocalReceiptPreviewEntry>();
  entriesForFile.add(entry);
  previewEntriesByFile.set(file, entriesForFile);

  const renderedPreview = renderedPdfPreviews.get(file);
  if (renderedPreview) {
    getOrCreatePreviewUrl(entry, renderedPreview);
  }
};

export const getLocalReceiptPreviewFile = (key: string): File | undefined => previewEntriesByKey.get(key)?.file;

export const getLocalReceiptPreviewUrl = (key: string): string | undefined => previewEntriesByKey.get(key)?.previewUrl;

export const promoteLocalReceiptPreview = (fileUri: string, documentId: string): void => {
  const entry = previewEntriesByKey.get(fileUri);
  if (!entry) {
    return;
  }

  entry.keys.add(documentId);
  previewEntriesByKey.set(documentId, entry);
};

const releasePreviewEntry = (entry: LocalReceiptPreviewEntry): void => {
  entry.keys.forEach((key) => previewEntriesByKey.delete(key));
  entry.keys.clear();
  detachPreviewFile(entry);

  if (entry.previewUrl && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(entry.previewUrl);
  }
};

export const releaseLocalReceiptPreview = (key: string): void => {
  const entry = previewEntriesByKey.get(key);
  if (entry) {
    releasePreviewEntry(entry);
  }
};

export const releaseAllLocalReceiptPreviews = (): void => {
  new Set(previewEntriesByKey.values()).forEach(releasePreviewEntry);
};

export const releaseLocalReceiptFile = (fileUri: string): void => {
  const entry = previewEntriesByKey.get(fileUri);
  if (entry) {
    entry.keys.delete(fileUri);
    previewEntriesByKey.delete(fileUri);
    if (entry.keys.size === 0) {
      releasePreviewEntry(entry);
    }
  }

  if (fileUri.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(fileUri);
  }
};
